import { useState, useMemo } from 'react';
import { FundingPolicyType } from '@shared';
import { insuranceCarrierDefs, InsuranceCarrier } from '@shared/insuranceCarrierDefs';
import { useBgaData } from './useBgaData';

interface UseAdvisorBGAProps {
  policyType?: FundingPolicyType;
}

export function useAdvisorBGA({ policyType }: UseAdvisorBGAProps = {}) {
  const [showRefreshMessage, setShowRefreshMessage] = useState(false);
  const { isLoading, hasError, getBgaForCarrier } = useBgaData();

  // Find the carrier associated with the policy type
  const policyCarrierId = useMemo(() => {
    if (!policyType) return null;

    const carrierEntry = Object.entries(insuranceCarrierDefs).find(([, def]) =>
      def.policyTypes.includes(policyType),
    );

    return carrierEntry ? (Number(carrierEntry[0]) as InsuranceCarrier) : null;
  }, [policyType]);

  // Get the BGA associated with this carrier
  const associatedBGA = useMemo(() => {
    if (!policyCarrierId) return null;
    return getBgaForCarrier(policyCarrierId);
  }, [policyCarrierId, getBgaForCarrier]);

  const hasBGAForCarrier = !!associatedBGA;
  const needsBGAConfiguration = policyType && policyCarrierId && !associatedBGA;

  const getCarrierName = () => {
    if (!policyCarrierId) return null;
    return insuranceCarrierDefs[policyCarrierId as InsuranceCarrier]?.name || null;
  };

  return {
    associatedBGA,
    policyCarrierId,
    hasBGAForCarrier,
    needsBGAConfiguration,
    showRefreshMessage,
    setShowRefreshMessage,
    isLoading,
    hasError,
    getCarrierName,
  };
}
